import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useConfigStore } from '@/store/config-store'
import { useBuildStore } from '@/store/build-store'
import { BuilderLayout } from '@/components/layout/BuilderLayout'
import { OptionCard } from '@/components/builder/OptionCard'
import { StepNav } from '@/components/builder/StepNav'
import { ConstraintWarnings } from '@/components/builder/ConstraintWarning'
import { lazy, Suspense } from 'react'
const ModelViewer = lazy(() =>
  import('@/components/builder/viewer/ModelViewer').then((m) => ({ default: m.ModelViewer }))
)
import { Button } from '@/components/ui/Button'
import { runConstraintEngine, resolveOptions } from '@/lib/constraint-engine'
import { trackStepViewed, trackOptionToggled } from '@/lib/tracking'

export default function Customize() {
  const navigate = useNavigate()
  const { config } = useConfigStore()
  const {
    selectedModelId,
    selectedOptionIds,
    currentCategoryIndex,
    setCategoryIndex,
    setSelectedOptionIds,
    setStep,
  } = useBuildStore()

  useEffect(() => {
    if (!selectedModelId) navigate('/build/model')
  }, [selectedModelId, navigate])

  useEffect(() => {
    if (selectedModelId) trackStepViewed('customize', selectedModelId)
  }, [currentCategoryIndex, selectedModelId])

  if (!config || !selectedModelId) return null

  const categories = config.categories
  const currentCategory = categories[currentCategoryIndex]
  const model = config.models.find((m) => m.id === selectedModelId)!
  const selectedOptions = config.options.filter((o) => selectedOptionIds.includes(o.id))

  const compatibleOptionIds = config.model_options
    .filter((mo) => mo.model_id === selectedModelId)
    .map((mo) => mo.option_id)

  const categoryOptions = config.options.filter(
    (o) => o.category_id === currentCategory.id && o.is_active && compatibleOptionIds.includes(o.id)
  )

  const constraintResult = runConstraintEngine(selectedModelId, selectedOptionIds, config.constraints)

  const handleToggle = (optionId: number) => {
    const next = resolveOptions(selectedOptionIds, optionId, config.constraints, selectedModelId)
    setSelectedOptionIds(next)
    trackOptionToggled(optionId, selectedModelId, !selectedOptionIds.includes(optionId))
  }

  const goNext = () => {
    if (currentCategoryIndex < categories.length - 1) {
      setCategoryIndex(currentCategoryIndex + 1)
    } else {
      setStep('review')
      navigate('/build/review')
    }
  }

  const goPrev = () => {
    if (currentCategoryIndex > 0) {
      setCategoryIndex(currentCategoryIndex - 1)
    } else {
      navigate('/build/model')
    }
  }

  const isLast = currentCategoryIndex === categories.length - 1

  return (
    <BuilderLayout>
      <StepNav
        categories={categories}
        currentIndex={currentCategoryIndex}
        completedBefore={currentCategoryIndex}
        onNavigate={setCategoryIndex}
      />

      {/* Mobile preview */}
      <div className="lg:hidden mb-5 bg-white rounded-xl border border-slate-200 overflow-hidden p-3">
        <Suspense fallback={<div className="aspect-[4/3] bg-slate-100 animate-pulse rounded-lg" />}>
          <ModelViewer model={model} selectedOptions={selectedOptions} />
        </Suspense>
      </div>

      <div className="flex gap-8">
        {/* Options column */}
        <div className="flex-1 min-w-0">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-slate-900">{currentCategory.name}</h2>
            {currentCategory.description && (
              <p className="text-slate-500 text-sm mt-1">{currentCategory.description}</p>
            )}
          </div>

          <ConstraintWarnings
            warnings={constraintResult.warnings}
            blockers={constraintResult.blockers}
          />

          {categoryOptions.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <p className="text-sm">No upgrades available in this category for your selected model.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {categoryOptions.map((option) => {
                const blocker = constraintResult.blockers.find((b) => b.optionId === option.id)
                const warning = constraintResult.warnings.find((w) => w.optionId === option.id)
                return (
                  <OptionCard
                    key={option.id}
                    option={option}
                    isSelected={selectedOptionIds.includes(option.id)}
                    isDisabled={constraintResult.disabled.includes(option.id)}
                    isBlocked={!!blocker}
                    blockMessage={blocker?.message}
                    warning={warning?.message}
                    onToggle={() => handleToggle(option.id)}
                  />
                )
              })}
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-slate-200">
            <Button variant="secondary" onClick={goPrev}>
              <ChevronLeft className="h-4 w-4" /> Back
            </Button>
            <Button onClick={goNext}>
              {isLast ? 'Review Build' : `Next: ${categories[currentCategoryIndex + 1]?.name ?? ''}`}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Desktop preview sidebar */}
        <div className="hidden lg:block w-72 xl:w-80 shrink-0">
          <div className="sticky top-6 bg-white rounded-xl border border-slate-200 overflow-hidden p-4">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Live Preview</div>
            <Suspense fallback={<div className="aspect-[4/3] bg-slate-100 animate-pulse rounded-lg" />}>
              <ModelViewer model={model} selectedOptions={selectedOptions} />
            </Suspense>
            {selectedOptions.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-xs text-slate-500 mb-1.5 font-medium">Selected upgrades</div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {selectedOptions.map((o) => (
                    <div key={o.id} className="text-xs text-slate-600 flex justify-between gap-2">
                      <span className="truncate">{o.name}</span>
                      <span className="text-slate-400 shrink-0">${o.price.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </BuilderLayout>
  )
}
